import React, { useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Badge } from 'react-bootstrap';

const Lesson04_UseEffect = () => {
  const [count, setCount] = useState(0);
  const [user, setUser] = useState('John');
  const [renderCount, setRenderCount] = useState(0);

  // Track renders (not in useEffect)
  React.useRef(0);
  const currentRender = ++React.useRef(0).current;

  // Effect 1: No dependency array - runs on EVERY render
  useEffect(() => {
    console.log('🔄 useEffect [NO DEPS] - Runs on EVERY render');
    console.log('   Current render:', currentRender);
    console.log('   Current count:', count, 'Current user:', user);
    console.log('   ⚠️ This can cause performance issues!');
  });

  // Effect 2: Empty dependency array [] - runs ONCE on mount
  useEffect(() => {
    console.log('🚀 useEffect [] - Runs ONCE on component mount');
    console.log('   This is like componentDidMount in class components');
    console.log('   Perfect for initial data fetching');
    
    // Cleanup function - runs on unmount
    return () => {
      console.log('🧹 useEffect [] - Cleanup on component unmount');
      console.log('   This is like componentWillUnmount');
    };
  }, []);

  // Effect 3: With [count] dependency - runs when COUNT changes
  useEffect(() => {
    console.log('👀 useEffect [count] - Runs when COUNT changes');
    console.log('   New count value:', count);
    console.log('   User value (not in deps):', user);
    
    return () => {
      console.log('🧹 useEffect [count] - Cleanup before next run');
      console.log('   Previous count was:', count);
    };
  }, [count]);

  // Effect 4: With [user] dependency - runs when USER changes
  useEffect(() => {
    console.log('👤 useEffect [user] - Runs when USER changes');
    console.log('   New user value:', user);
    console.log('   Count value (not in deps):', count);
  }, [user]);

  // Effect 5: Multiple dependencies [count, user] - runs when EITHER changes
  useEffect(() => {
    console.log('👥 useEffect [count, user] - Runs when COUNT OR USER changes');
    console.log('   Count:', count, 'User:', user);
  }, [count, user]);

  useEffect(() => {
    console.log('📖 Lesson 04: useEffect Hook - Loaded');
    console.log('📚 Watch the console carefully as you interact with buttons!');
  }, []);

  const incrementCount = () => {
    console.log('');
    console.log('━━━━━━━━ USER ACTION: Incrementing Count ━━━━━━━━');
    setCount(prevCount => prevCount + 1);
  };

  const toggleUser = () => {
    console.log('');
    console.log('━━━━━━━━ USER ACTION: Toggling User ━━━━━━━━');
    setUser(prevUser => prevUser === 'John' ? 'Jane' : 'John');
  };

  const forceRerender = () => {
    console.log('');
    console.log('━━━━━━━━ USER ACTION: Force Re-render ━━━━━━━━');
    setRenderCount(prev => prev + 1);
  };

  return (
    <LessonLayout 
      lessonNumber={4}
      title="useEffect Hook - Dependency Arrays" 
      description="Understanding when useEffect runs based on different dependency array patterns"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>useEffect with no dependencies (runs every render)</li>
          <li>useEffect with [] (runs once on mount)</li>
          <li>useEffect with dependencies (runs when dependencies change)</li>
          <li>Cleanup functions in useEffect</li>
          <li>Multiple useEffect hooks in one component</li>
        </ul>
      </Alert>

      <Alert variant="danger" className="mt-3">
        <strong>⚠️ IMPORTANT:</strong> Open your browser console RIGHT NOW (F12) to see the useEffect behavior!
      </Alert>

      {/* Interactive Controls */}
      <Row className="mt-4">
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header className="bg-primary text-white">Counter State</Card.Header>
            <Card.Body className="text-center">
              <h2 className="display-4">{count}</h2>
              <Button 
                variant="primary" 
                onClick={incrementCount}
                className="w-100"
              >
                Increment Counter
              </Button>
              <small className="text-muted d-block mt-2">
                Triggers: useEffect [count] and [count, user]
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-3">
            <Card.Header className="bg-success text-white">User State</Card.Header>
            <Card.Body className="text-center">
              <h2 className="display-4">{user}</h2>
              <Button 
                variant="success" 
                onClick={toggleUser}
                className="w-100"
              >
                Toggle User
              </Button>
              <small className="text-muted d-block mt-2">
                Triggers: useEffect [user] and [count, user]
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-3">
            <Card.Header className="bg-warning">Force Re-render</Card.Header>
            <Card.Body className="text-center">
              <h2 className="display-4">{renderCount}</h2>
              <Button 
                variant="warning" 
                onClick={forceRerender}
                className="w-100"
              >
                Force Re-render
              </Button>
              <small className="text-muted d-block mt-2">
                Triggers: useEffect with NO deps
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* useEffect Patterns Explanation */}
      <h4 className="mt-4">📊 useEffect Dependency Array Patterns</h4>
      <Card className="mb-3">
        <Card.Body>
          <table className="table comparison-table">
            <thead>
              <tr>
                <th width="20%">Pattern</th>
                <th width="25%">Syntax</th>
                <th width="25%">When It Runs</th>
                <th width="30%">Use Case</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><Badge bg="danger">No Deps</Badge></td>
                <td><code>useEffect(() =&gt; {'{}'})</code></td>
                <td>Every render</td>
                <td>⚠️ Rarely used - can cause performance issues</td>
              </tr>
              <tr>
                <td><Badge bg="success">Empty []</Badge></td>
                <td><code>useEffect(() =&gt; {'{}'}, [])</code></td>
                <td>Once on mount</td>
                <td>✅ API calls, subscriptions, timers</td>
              </tr>
              <tr>
                <td><Badge bg="primary">With Deps</Badge></td>
                <td><code>useEffect(() =&gt; {'{}'}, [dep])</code></td>
                <td>When dep changes</td>
                <td>✅ Sync with props/state, side effects</td>
              </tr>
              <tr>
                <td><Badge bg="info">Multiple Deps</Badge></td>
                <td><code>useEffect(() =&gt; {'{}'}, [a, b])</code></td>
                <td>When any dep changes</td>
                <td>✅ React to multiple values</td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      {/* Code Examples */}
      <h4 className="mt-4">💻 Code Examples</h4>
      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Pattern 1: Run Once on Mount</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`useEffect(() => {
  // Fetch data, setup subscriptions
  console.log('Component mounted');
  
  return () => {
    // Cleanup: unsubscribe, cancel timers
    console.log('Component unmounting');
  };
}, []); // ← Empty array = run once`}
              </pre>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Pattern 2: Run on Dependency Change</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`useEffect(() => {
  // This runs when 'count' changes
  console.log('Count changed:', count);
  
  return () => {
    // Cleanup from previous effect
    console.log('Cleanup for old count');
  };
}, [count]); // ← Run when count changes`}
              </pre>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Pattern 3: Multiple Dependencies</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`useEffect(() => {
  // Runs when count OR user changes
  console.log('State changed:', {count, user});
}, [count, user]); // ← Multiple deps`}
              </pre>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Pattern 4: No Dependencies (Avoid)</Card.Header>
            <Card.Body>
              <pre className="code-example">
{`useEffect(() => {
  // ⚠️ Runs on EVERY render
  // Usually not what you want!
  console.log('Every render');
}); // ← No array = every render`}
              </pre>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practices:</strong>
        <ul className="mb-0">
          <li>Always include all values from component scope that change over time and are used by the effect</li>
          <li>Use [] for effects that should only run on mount (like data fetching)</li>
          <li>Return a cleanup function when setting up subscriptions, timers, or event listeners</li>
          <li>Avoid omitting the dependency array unless you specifically need every render</li>
          <li>ESLint plugin (eslint-plugin-react-hooks) helps catch missing dependencies</li>
        </ul>
      </Alert>

      <Alert variant="info" className="mt-3">
        <strong>🎯 Try This:</strong>
        <ol className="mb-0">
          <li>Click "Increment Counter" and watch which useEffects run in the console</li>
          <li>Click "Toggle User" and observe the different behavior</li>
          <li>Click "Force Re-render" to see the no-dependency useEffect trigger</li>
          <li>Notice the cleanup functions logging before the next effect runs</li>
        </ol>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson04_UseEffect;
